import { useEffect, useMemo, useRef } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { AddButton } from "~/components/add-button";
import { Home as HomeIcon } from "@mui/icons-material";
import { useSearch } from "~/components/search/useSearch";
import { FilterBar } from "~/components/FilterBar/FilterBar";
import { Space } from "~/lib/types";
import {
  useGetWorkspacesGlobalCountQuery,
  useGetWorkspacesQuery,
} from "~/operations";
import { Flex } from "~/components/Flex";
import EmptyWorkspaces from "~/images/empty-workspaces.png";
import EmptyWorkspacesLight from "~/images/empty-workspaces-light.png";
import { Metrics } from "~/lib/arrow";
import { setDocumentTitle } from "~/lib/commonUtils";
import { useColorMode } from "~/providers/color-mode";
import { CanCreateWorkspace } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/CreateWorkspace";
import { ViewSwitcher, ViewType } from "~/components/view-switcher";
import { SpaceSummaryList } from "~/components/SpaceSummary/SpaceSummaryList";
import { NoSearchResults } from "~/pages/Search";
import { getDailyCountsTimeline } from "~/components/PriorityFindings/useDailyCounts";

interface WorkspacesEntryPageProps {
  space: Space;
}

export function WorkspacesEntryPage({ space }: WorkspacesEntryPageProps) {
  const { mode } = useColorMode();
  const search = useSearch();

  // cache the globalcount so that we can prevent flashes of content of the filterbar between searches
  const cachedGlobalCount = useRef<number | null>(null);
  const hasLoadedOnce = useRef(false);

  const views = [ViewType.Cards, ViewType.List];

  const { startTime, endTime } = useMemo(() => getDailyCountsTimeline(28), []);

  const { data, loading, fetchMore } = useGetWorkspacesQuery({
    variables: {
      input: {
        scopeMrn: space.mrn,
        query: search.searchFilters.join(" "),
      },
      metricMrns: [
        Metrics.AssetsRiskRatingDistribution,
        Metrics.HistoricalDailyAssetCount,
        Metrics.HistoricalDailyFindingCount,
      ],
      startTime,
      endTime,
    },
    fetchPolicy: "cache-and-network",
  });

  const { data: globalCountData } = useGetWorkspacesGlobalCountQuery({
    variables: {
      input: {
        scopeMrn: space.mrn,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const { totalCount: globalCount = 0 } = globalCountData?.workspaces || {};
  const { edges = [], totalCount = 0, pageInfo } = data?.workspaces || {};

  useEffect(() => {
    if (globalCount > 0 && !hasLoadedOnce.current) {
      cachedGlobalCount.current = globalCount;
      hasLoadedOnce.current = true;
    }
  }, [globalCount, space.mrn]);

  const effectiveGlobalCount = cachedGlobalCount.current ?? globalCount;
  const hasWorkspaces = effectiveGlobalCount > 0;
  const hasFilteredWorkspaces = totalCount > 0;

  const spaceSummaries =
    edges?.map(({ node }) => {
      const metricsEntries =
        node.metrics?.__typename === "MetricsResponse"
          ? (node.metrics.entries ?? undefined)
          : undefined;
      return {
        spaceId: space.id,
        workspaceId: node.mrn,
        ...node,
        metricsEntries,
      };
    }) || [];

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/space/overview?spaceId=${space.id}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Typography key="/space/workspaces">Workspaces</Typography>,
  ];

  setDocumentTitle(["Workspaces", space.name]);

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3, overflowWrap: "anywhere" }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>
      {/* Heading */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
        <Grid size={12}>
          <Typography
            variant="h4"
            component="h1"
            fontWeight={700}
            textTransform="uppercase"
          >
            Workspaces
          </Typography>
        </Grid>
        <CanCreateWorkspace>
          <Grid size={12}>
            <AddButton
              id="space"
              href={`/space/workspaces/new?spaceId=${space.id}`}
            />
          </Grid>
        </CanCreateWorkspace>
      </Box>
      {hasWorkspaces && (
        <Grid container sx={{ mb: 3 }}>
          <Grid container size={12} gap={3} alignItems="flex-start">
            <Grid size="grow">
              <FilterBar
                searchId="workspaces-search"
                placeholder="workspaces"
                search={search}
                mb={0}
              />
            </Grid>
            <Grid size="auto" sx={{ pt: 0.37 }}>
              <ViewSwitcher views={views} defaultView={views[0]} />
            </Grid>
          </Grid>
        </Grid>
      )}
      {!loading && !hasWorkspaces && (
        <Flex
          className="empty-workspaces"
          sx={{ flexDirection: "column", justifyContent: "center", gap: 6 }}
        >
          <Box
            component="img"
            src={mode === "light" ? EmptyWorkspacesLight : EmptyWorkspaces}
            alt="Empty workspaces illustration"
            width={380}
            sx={{ mx: "auto", mt: 10 }}
          />
          <Flex sx={{ flexDirection: "column", justifyContent: "center" }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                textAlign: "center",
                textTransform: "uppercase",
              }}
            >
              <CanCreateWorkspace fallback="No workspaces available">
                Create your first workspace!
              </CanCreateWorkspace>
            </Typography>
            <CanCreateWorkspace>
              <Typography color="text.secondary" sx={{ textAlign: "center" }}>
                Structure projects, monitor a specific set of assets, or do
                something entirely different -- it's up to you!
                <br /> A workspace dynamically pulls in all assets in this space
                that match criteria you define.
              </Typography>
              <Button
                component={RouterLink}
                to={`/space/workspaces/new?spaceId=${space.id}`}
                sx={{
                  mx: "auto",
                  mt: 3,
                  px: 2,
                  background: (theme) => theme.palette.primary.gradient,
                  color: "common.white",
                }}
              >
                Create Workspace
              </Button>
            </CanCreateWorkspace>
          </Flex>
        </Flex>
      )}
      {!loading && hasFilteredWorkspaces && (
        <SpaceSummaryList
          className="workspaces"
          views={views}
          spaceSummaries={spaceSummaries}
          PaginationProps={{
            fetchMore,
            pageInfo,
            totalCount,
          }}
        />
      )}
      {!loading && hasWorkspaces && !hasFilteredWorkspaces && (
        <NoSearchResults
          searchQuery={search.searchFilters.join()}
          name={space.name}
        />
      )}
    </Box>
  );
}
